import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { AbsenceType, type NurseInShift } from "../../../../utils/models";
import { InlineHint } from "../../../UIToolkit/molecules/InlineHint";
import SystemDialog from "../../../UIToolkit/Popups/Dialog";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import { GAPS_DAY_LABEL_FORMAT, joinLabelParts } from "../gapsLabels";
import { GapsRemovalCallInTime } from "./GapsRemovalCallInTime";
import { GapsRemovalMessageField } from "./GapsRemovalMessageField";
import { GapsRemovalPayrollAck } from "./GapsRemovalPayrollAck";
import { GapsRemovalReasonPicker } from "./GapsRemovalReasonPicker";
import { GapsRemovalReplacementRow } from "./GapsRemovalReplacementRow";
import type { GapsGroupTotals, GapsRemovalOutcome, GapsRemovalSource, GapsSheetGroupLabel } from "./gapsSheetTypes";
import { useGapsRemoval } from "./useGapsRemoval";

type Props = {
	nurseInShift: NurseInShift;
	/** The name the day's rows print for this person. */
	name: string;
	/** The group the person is placed by: its label for the heading, its key for the cover prompt. */
	group: GapsSheetGroupLabel;
	/** The person's own hours, as the row prints them. */
	hoursLabel: string;
	/** The group's totals right now, so the cover prompt can show what the removal took away. */
	totalsBefore: GapsGroupTotals | null;
	source: GapsRemovalSource;
	/** The footer's way out: back to the staff row's actions, or back to the call-in search. */
	onBack: () => void;
	onRemoved: (outcome: GapsRemovalOutcome) => void;
};

const SHEET = "gaps-removal";

/**
 * "Why is this person off?" — the removal, for both Remove on a staff row and Log a call-in (P3-3).
 *
 * All of it is `useDeleteRecurringOrSingleState`, the desktop removal dialog's own state (through `useGapsRemoval`): the
 * reason, the payroll acknowledgement, the call-off agent's find-a-replacement, the call-in time for the Call-In
 * reason, the union warning, and the write itself. This step only lays those out for a phone, and asks twice for a
 * removal a week or more out.
 */
export const GapsRemovalStep: FC<Props> = ({ nurseInShift, name, group, hoursLabel, totalsBefore, source, onBack, onRemoved }) => {
	const { t } = useTranslation();
	const { removal, reasons, showsReasons, isOther, canConfirm, daysOut, confirm, pickReason, runRemoval, isFarOutConfirmOpen, setIsFarOutConfirmOpen } =
		useGapsRemoval({ nurseInShift, name, group, totalsBefore, source, onBack, onRemoved });

	// The person's own hours only when they differ from the window's — "Nurse 6a-6:30p · 6a-6:30p" says nothing twice.
	const ownHours = hoursLabel === group.timeLabel ? "" : hoursLabel;
	const subtitle = joinLabelParts([`${group.jobLabel} ${group.timeLabel}`, ownHours, nurseInShift.startTime.toFormat(GAPS_DAY_LABEL_FORMAT)]);

	return (
		<>
			<SheetStep
				stepKey="removal"
				dataTestId={SHEET}
				title={t("gaps.callIn.removal.title", { name })}
				subtitle={subtitle}
				footer={
					<>
						<SheetStepButton tone="dismiss" onClick={onBack} dataTestId={`${SHEET}-cancel`}>
							{t("back")}
						</SheetStepButton>
						<SheetStepButton tone="delete" onClick={confirm} disabled={!canConfirm} loading={removal.isLoading} dataTestId={`${SHEET}-confirm`}>
							{t("gaps.callIn.removal.confirm")}
						</SheetStepButton>
					</>
				}
			>
				<Box sx={{ px: 2, pt: 1.75, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
					{showsReasons && (
						<GapsRemovalReasonPicker
							reasons={reasons}
							selectedReason={removal.selectedReason}
							shakeControls={removal.reasonShakeControls}
							onPick={pickReason}
							testIdPrefix={SHEET}
						/>
					)}
					<InlineHint open={removal.isSelfSchedulingShift} tone="info" dataTestId={`${SHEET}-self-scheduling`}>
						{t("gaps.callIn.removal.selfScheduling")}
					</InlineHint>
					<GapsRemovalMessageField
						isOpen={showsReasons && removal.selectedReason !== undefined}
						isOther={isOther}
						message={removal.removalMessage}
						onMessageChange={removal.setRemovalMessage}
						testIdPrefix={SHEET}
					/>
					{/* Exactly when the hook sends a call-in time: the reason is the Call-In label itself. */}
					{removal.selectedReason === AbsenceType.CALL_IN && (
						<GapsRemovalCallInTime callInTime={removal.callInTime} onCallInTimeChange={removal.setCallInTime} testIdPrefix={SHEET} />
					)}
					{removal.needsPayrollConfirmation && (
						<GapsRemovalPayrollAck
							isAcknowledged={removal.deleteValid === true}
							onAcknowledgedChange={removal.setDidConfirmPayrollWarning}
							testIdPrefix={SHEET}
						/>
					)}
					<InlineHint open={removal.shouldWarnRemovingUnion} tone="warning" dataTestId={`${SHEET}-union`}>
						{t("gaps.callIn.removal.union", { name })}
					</InlineHint>
				</Box>

				{removal.renderFindReplacementForShift && <GapsRemovalReplacementRow removal={removal} testIdPrefix={SHEET} />}
			</SheetStep>

			<SystemDialog
				dataTestId={`${SHEET}-far-out-dialog`}
				open={isFarOutConfirmOpen}
				onClose={() => setIsFarOutConfirmOpen(false)}
				title={t("gaps.callIn.removal.farOutTitle", { name, count: daysOut })}
				body={t("gaps.callIn.removal.farOutBody")}
				confirm={t("gaps.callIn.removal.farOutConfirm")}
				confirmButtonVariant="delete"
				onConfirm={() => {
					setIsFarOutConfirmOpen(false);
					void runRemoval();
				}}
			/>
		</>
	);
};
